<script lang="ts" setup>
import { computed, type VNodeChild } from 'vue';
import { IconBulb, IconClose, IconAlertOctagonFilled } from '@/icons';
import { hasSlotContent } from '../../utils';
import { UiButtonIcon } from '../ui-button-icon';

interface Props {
  closable?: boolean;
  rich?: boolean;
  variant?: 'success' | 'warning' | 'error';
  preset?: 'hint' | 'error';
  rounded?: boolean;
}

const { rounded = true, rich = false, closable = false, ...props } = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

defineSlots<{
  default?: () => VNodeChild;
  icon?: () => VNodeChild;
  text?: () => VNodeChild;
  action?: () => VNodeChild;
}>();

function close() {
  emit('close');
}

const variant = computed(() => {
  if (props.variant) {
    return props.variant;
  }

  if (props.preset === 'hint') {
    return null;
  }

  if (props.preset === 'error') {
    return 'error';
  }

  return null;
});

const icon = computed(() => {
  if (props.preset === 'hint') {
    return IconBulb;
  }

  if (props.preset === 'error') {
    return IconAlertOctagonFilled;
  }

  return null;
});
</script>

<template>
  <div
    :class="[
      $style.root,
      {
        [$style.variantSuccess]: variant === 'success',
        [$style.variantWarning]: variant === 'warning',
        [$style.variantError]: variant === 'error',
        [$style.rich]: rich,
        [$style.closable]: closable,
        [$style.rounded]: rounded,
      },
    ]"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
  >
    <template v-if="rich">
      <div :class="$style.head">
        <div v-if="icon || hasSlotContent($slots.icon) || icon" :class="$style.icon">
          <slot name="icon">
            <component :is="icon" v-if="icon" aria-hidden="true" />
          </slot>
        </div>
        <div v-if="hasSlotContent($slots.default)" :class="$style.title"><slot /></div>
      </div>
      <div v-if="hasSlotContent($slots.text)" :class="$style.text"><slot name="text" /></div>
      <div v-if="hasSlotContent($slots.action)" :class="$style.action"><slot name="action" /></div>
    </template>
    <template v-else>
      <div v-if="icon || hasSlotContent($slots.icon)" :class="$style.icon">
        <slot name="icon">
          <component :is="icon" v-if="icon" aria-hidden="true" />
        </slot>
      </div>
      <div :class="$style.content">
        <div v-if="hasSlotContent($slots.default)" :class="$style.title"><slot /></div>
        <div v-if="hasSlotContent($slots.text)" :class="$style.text"><slot name="text" /></div>
      </div>
      <div v-if="hasSlotContent($slots.action)" :class="$style.action"><slot name="action" /></div>
    </template>
    <div v-if="closable" :class="$style.close">
      <UiButtonIcon variant="tertiary" @click="close">
        <IconClose aria-hidden="true" />
      </UiButtonIcon>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  position: relative;
  font-family: typography.$font-family-primary;
  font-size: 13px;
  line-height: 16px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  background: rgba(160, 120, 169, 0.12);
  color: #896292;
  gap: 12px;
  min-height: 40px;
  box-sizing: border-box;

  &.rounded {
    border-radius: 10px;
  }

  &.closable {
    padding-right: 48px;
  }
}

.icon {
  width: 20px;
  height: 20px;
  flex: none;

  > svg {
    width: 100%;
    height: 100%;
  }
}

.content {
  flex-basis: 0;
  flex-grow: 1;
  gap: 8px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
}

.action {
  flex: none;
}

.title,
.text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title {
  font-weight: 600;
}

.text {
  font-weight: 500;
  color: #655e60;
}

.close {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}

.rich {
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  .content {
    grid-template-columns: auto;
    grid-auto-rows: auto;
  }

  .text {
    white-space: normal;
  }

  .close {
    top: 8px;
    transform: none;
  }
}

.head {
  display: grid;
  align-items: center;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 6px;
}

.variantSuccess {
  background-color: rgba(24, 165, 130, 0.12);
  color: #00906e;
}

.variantWarning {
  background-color: rgba(228, 161, 75, 0.12);
  color: #c27d0d;
}

.variantError {
  background-color: rgba(230, 57, 70, 0.12);
  color: #b2001b;
}
</style>
